import { useMemo } from 'react';
import { useAuth } from '@/hooks/auth/useAuth';
import { usePermissions } from '@/hooks/auth/usePermissions';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { useIsWarrantyPolicyOwner } from './useIsWarrantyPolicyOwner';
import { useWarrantyScopeSocietyId } from './useWarrantyScopeSocietyId';
import type { WarrantyPermissions } from '../types/warranty-permissions.types';

/**
 * Hook pour gérer les permissions liées aux garanties
 * Déduit les permissions depuis les rôles de l'utilisateur et la configuration société
 * Quand la société n'est pas propriétaire de la stratégie garantie (warrantyPolicyOwnerId = parent),
 * les permissions de gestion (edit, provisions, etc.) sont forcées à false.
 */
export const useWarrantyPermissions = (societyId?: string): WarrantyPermissions => {
  const { user } = useAuth();
  const { can } = usePermissions();
  const { data: effectiveConfig } = useEffectiveConfiguration(societyId);
  const scopeSocietyId = useWarrantyScopeSocietyId(societyId);
  const isOwner = useIsWarrantyPolicyOwner(user?.type === 'EXTERNAL' ? scopeSocietyId : undefined);

  const permissions = useMemo<WarrantyPermissions>(() => {
    if (!user) {
      return {
        canModifyDefaultParts: false,
        canSelectAllParts: false,
        canEdit: false,
        canEditPrice: false,
        canDownloadCgp: false,
        canViewDiagnostic: false,
        canUpdateProvisions: false,
      };
    }

    // Tous les utilisateurs internes ont toutes les permissions
    const isInternal = user.type === 'INTERNAL';

    return {
      canModifyDefaultParts: isInternal || !!effectiveConfig?.canModifyMandatoryPieces,
      canSelectAllParts: isInternal,
      canEdit: can('warranty.edit') && (isInternal || isOwner),
      canEditPrice: can('warranty.edit_price') && (isInternal || isOwner || !effectiveConfig?.lockChildrenPriceEditing),
      canDownloadCgp: can('warranty.download_cgp'),
      canViewDiagnostic: isInternal && can('warranty.view_diagnostic'),
      canUpdateProvisions: can('warranty.update_provisions') && (isInternal || isOwner),
    };
  }, [user, can, effectiveConfig, isOwner]);

  return permissions;
};
